import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Mail, Instagram, Phone, HelpCircle, Coins, Ticket, MapPin, Trash2,
  CreditCard, Music, Shield, Calendar, Users, AlertTriangle
} from 'lucide-react';
import axios from 'axios';
import useLang from '../hooks/useLang';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Pick an icon based on keywords in the question (FR or any language)
const pickIcon = (question = '') => {
  const q = question.toLowerCase();
  if (/harc|intimid|harass|inapprop|signal|report/.test(q)) return AlertTriangle;
  if (/repos|rest|rustruimt|descanso|safe|s\u00e9cur|security|shield/.test(q)) return Shield;
  if (/acc(è|e)d|congr(è|e)s|transport|getting|toegang|acceso|metro|gare/.test(q)) return MapPin;
  if (/programmat|dj|line.?up|musi[ck]|reggaeton|muzi/.test(q)) return Music;
  if (/ticket|entr(e|é)e|billet|buy|acheter/.test(q)) return Ticket;
  if (/vip|table|r(é|e)serv|book/.test(q)) return CreditCard;
  if (/coin|point|fidelit|r(e|é)compens|reward/.test(q)) return Coins;
  if (/compte|account|supprim|delete|rgpd|privacy/.test(q)) return Trash2;
  if (/date|quand|when|heure|when|agenda|calendar/.test(q)) return Calendar;
  if (/equipe|team|organis|who|commun/.test(q)) return Users;
  return HelpCircle;
};

const SupportPage = () => {
  const { t } = useTranslation();
  const lang = useLang();
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios
      .get(`${BACKEND_URL}/api/faqs?lang=${lang}`)
      .then((r) => {
        const items = (r.data?.faqs || []).map((f) => ({
          question: f.question,
          answer: f.answer,
          icon: pickIcon(f.question),
        }));
        setFaqs(items);
      })
      .catch(() => setFaqs([]))
      .finally(() => setLoading(false));
  }, [lang]);

  return (
    <div className="min-h-screen bg-[#060606] pt-48 pb-16">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#C9A84C]/20 mb-6">
            <HelpCircle className="w-8 h-8 text-[#C9A84C]" />
          </div>
          <h1 className="font-display text-4xl sm:text-5xl text-[#F5F0E8] mb-4">
            Centre d'Aide
          </h1>
          <p className="text-xl text-[#B9B2A8]">
            Besoin d'aide ?
          </p>
          <p className="text-[#B9B2A8] mt-2">
            Notre équipe est là pour vous aider avec toutes vos questions concernant l'application, les événements, les réservations et plus encore.
          </p>
        </div>

        {/* Contact Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          {/* Email */}
          <a
            href="mailto:info@invasionlatina.be"
            className="group bg-[#0B0B0C] border border-white/10 rounded-xl p-6 hover:border-[#C9A84C]/50 transition-all"
            data-testid="support-email"
          >
            <div className="flex flex-col items-center text-center">
              <div className="w-12 h-12 rounded-full bg-[#C9A84C]/20 flex items-center justify-center mb-4 group-hover:bg-[#C9A84C]/30 transition-colors">
                <Mail className="w-6 h-6 text-[#C9A84C]" />
              </div>
              <h3 className="font-display text-xl text-[#F5F0E8] mb-2">Email</h3>
              <p className="text-[#C9A84C] text-sm mb-1">info@invasionlatina.be</p>
              <p className="text-xs text-[#B9B2A8]">Réponse sous 24-48h</p>
            </div>
          </a>

          {/* Instagram */}
          <a
            href="https://instagram.com/invasionlatina.be"
            target="_blank"
            rel="noopener noreferrer"
            className="group bg-[#0B0B0C] border border-white/10 rounded-xl p-6 hover:border-[#C9A84C]/50 transition-all"
            data-testid="support-instagram"
          >
            <div className="flex flex-col items-center text-center">
              <div className="w-12 h-12 rounded-full bg-[#C9A84C]/20 flex items-center justify-center mb-4 group-hover:bg-[#C9A84C]/30 transition-colors">
                <Instagram className="w-6 h-6 text-[#C9A84C]" />
              </div>
              <h3 className="font-display text-xl text-[#F5F0E8] mb-2">Instagram</h3>
              <p className="text-[#C9A84C] text-sm mb-1">@invasionlatina.be</p>
              <p className="text-xs text-[#B9B2A8]">Envoyez-nous un DM</p>
            </div>
          </a>

          {/* WhatsApp */}
          <a
            href="https://wa.me/32478814497"
            target="_blank"
            rel="noopener noreferrer"
            className="group bg-[#0B0B0C] border border-white/10 rounded-xl p-6 hover:border-[#C9A84C]/50 transition-all"
            data-testid="support-whatsapp"
          >
            <div className="flex flex-col items-center text-center">
              <div className="w-12 h-12 rounded-full bg-[#C9A84C]/20 flex items-center justify-center mb-4 group-hover:bg-[#C9A84C]/30 transition-colors">
                <Phone className="w-6 h-6 text-[#C9A84C]" />
              </div>
              <h3 className="font-display text-xl text-[#F5F0E8] mb-2">WhatsApp</h3>
              <p className="text-[#C9A84C] text-sm mb-1">Contactez-nous</p>
              <p className="text-xs text-[#B9B2A8]">Pour les réservations de tables</p>
            </div>
          </a>
        </div>

        {/* FAQ Section */}
        <div>
          <h2 className="font-display text-3xl text-[#F5F0E8] mb-8 text-center">
            Questions Fréquentes
          </h2>
          <div className="space-y-4">
            {loading && (
              <div className="text-center text-[#B9B2A8] py-8">Chargement…</div>
            )}
            {!loading && faqs.length === 0 && (
              <div className="text-center text-[#B9B2A8] py-8">
                Aucune question fréquente pour le moment.
              </div>
            )}
            {faqs.map((faq, index) => {
              const IconComponent = faq.icon;
              return (
                <div
                  key={index}
                  className="bg-[#0B0B0C] border border-white/10 rounded-xl p-6 hover:border-[#C9A84C]/30 transition-all"
                  data-testid={`faq-item-${index}`}
                >
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-[#C9A84C]/20 flex items-center justify-center flex-shrink-0">
                      <IconComponent className="w-5 h-5 text-[#C9A84C]" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-display text-lg text-[#F5F0E8] mb-2">
                        {faq.question}
                      </h3>
                      <p className="text-[#B9B2A8] text-sm leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Additional Help */}
        <div className="mt-16 text-center">
          <div className="bg-gradient-to-br from-[#C9A84C]/20 to-transparent border border-[#C9A84C]/30 rounded-xl p-8">
            <h3 className="font-display text-2xl text-[#F5F0E8] mb-4">
              Vous n'avez pas trouvé votre réponse ?
            </h3>
            <p className="text-[#B9B2A8] mb-6">
              Notre équipe est disponible pour répondre à toutes vos questions.
            </p>
            <a
              href="mailto:info@invasionlatina.be"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#C9A84C] text-[#060606] font-display text-sm uppercase tracking-wider rounded-lg hover:bg-[#B39840] transition-colors"
              data-testid="support-cta"
            >
              <Mail className="w-4 h-4" />
              Contactez-nous
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupportPage;